import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";

/**
 * The supplier's rides — booked here for a passenger, cloned from the web
 * app's booking flow (no agents or corporates).
 *
 * Everything lives under the booking service's /supplier-bookings routes. The
 * server resolves the supplier from the JWT, stamps supplier_id on the ride
 * and scopes every list / detail / cancel to it, so the client never sends a
 * supplier id. The passenger (name + mobile) becomes the ride's rider: they
 * get the OTP and driver updates, exactly as if they had booked themselves.
 */

export interface TripPoint {
  lat: number;
  lon: number;
  add: string;
  label?: string;
}

export interface TripRequest {
  pickup: TripPoint;
  /** Via stops first, destination last. */
  drop: TripPoint[];
  booking: "now" | "schedule";
  /** ISO string; only for booking === "schedule". */
  booking_time?: string;
  wheelchair_access?: boolean;
  pets_allowed?: boolean;
  airport_service?: boolean;
  flight_number?: string;
  trunk_luggage?: number | null;
  hand_luggage?: number | null;
}

export interface BookRideRequest extends TripRequest {
  car_type_id: number;
  passenger: { full_name: string; country_code: string; mobile: string };
  /** Offer the ride to one of the supplier's own drivers (a_customers.id). */
  driver_id?: string | null;
  /** What the passenger pays, instead of the quote. Only with driver_id. */
  fixed_amount?: number | null;
}

/** One of the supplier's drivers, as /supplier-bookings/drivers returns it. */
export interface MyDriver {
  cust_id: string;
  name: string;
  profile_img: string | null;
  rating: number | null;
  online: boolean;
  car_type_id: number | null;
  car_type: string | null;
  capacity: number | null;
  vehicle: string | null;
  distance_m: number | null;
  available: boolean;
  /** Why the driver can't take this ride (offline, on a ride, vehicle too small…). */
  reason: string | null;
}

export interface MyDriversRequest {
  pickup: TripPoint;
  booking: "now" | "schedule";
  car_type_id?: number | null;
  wheelchair_access?: boolean;
  pets_allowed?: boolean;
}

/** One car option from the fare quote (fareCalculation's `prices` row). */
export interface CarQuote {
  carTypeId: number;
  amount: number;
  meterPrice: "approx" | "fixed" | string;
  title: string;
  description?: string;
  image?: string;
  capacity?: number;
  trunk_luggage?: number;
  hand_luggage?: number;
  data?: any;
}

export const estimateRide = apiThunk<any, TripRequest>("bookings/estimate", {
  server: "taxi",
  url: "supplier-bookings/estimate",
  method: "POST",
  errorMsg: true,
});

export const loadMyDrivers = apiThunk<any, MyDriversRequest>("bookings/myDrivers", {
  server: "taxi",
  url: "supplier-bookings/drivers",
  method: "POST",
});

/** One of the supplier's drivers on the Live Map. */
export interface LiveDriver {
  cust_id: string;
  name: string;
  profile_img: string | null;
  mobile: string | null;
  vehicle: string | null;
  car_type: string | null;
  status: "available" | "on_ride" | "offline";
  position: { lat: number; lon: number } | null;
  last_seen: string | null;
  /** The ride they're on — the supplier's own (`yours`) or a platform ride. */
  ride: { id: number; booking_uni_code: string; status: string; yours: boolean } | null;
}

/** One of the supplier's rides not yet finished. */
export interface LiveRide {
  id: number;
  booking_uni_code: string;
  status: string;
  rider_name: string;
  driver_id: string | null;
  driver_name: string | null;
  /** Pending and offered to one of the supplier's drivers. */
  offered_to: string | null;
  own_driver: boolean;
  pickup: any;
  drop: any;
  estimated_amount: number;
  amount_type: string;
  booking_time: string;
  bookingzone: any;
  driver_position: { lat: number; lon: number } | null;
  driver_last_seen: string | null;
}

export const loadLiveBoard = apiThunk<any, void>("bookings/live", {
  server: "taxi",
  url: "supplier-bookings/live",
  method: "GET",
});

export const bookRide = apiThunk<any, BookRideRequest>("bookings/book", {
  server: "taxi",
  url: "supplier-bookings/save",
  method: "POST",
  successMsg: true,
  errorMsg: true,
});

export const loadBookings = apiThunk<any, { status?: string; page?: number; size?: number }>(
  "bookings/load",
  {
    server: "taxi",
    url: "supplier-bookings/list",
    method: "GET",
    params: (arg) => arg,
    errorMsg: true,
  }
);

export const loadScheduledRides = apiThunk<any, { page?: number; size?: number } | void>(
  "bookings/loadScheduled",
  {
    server: "taxi",
    url: "supplier-bookings/scheduled",
    method: "GET",
    params: (arg) => (arg ? arg : undefined),
    errorMsg: true,
  }
);

export const loadStatusCount = apiThunk<any, void>("bookings/statusCount", {
  server: "taxi",
  url: "supplier-bookings/status-count",
  method: "GET",
});

export const loadBooking = apiThunk<any, { bookingId: number | string }>("bookings/loadOne", {
  server: "taxi",
  url: ({ bookingId }) => `supplier-bookings/${bookingId}`,
  method: "GET",
  errorMsg: true,
});

export const loadScheduledRide = apiThunk<any, { scheduleId: number | string }>(
  "bookings/loadScheduledOne",
  {
    server: "taxi",
    url: ({ scheduleId }) => `supplier-bookings/scheduled/${scheduleId}`,
    method: "GET",
    errorMsg: true,
  }
);

export const cancelBooking = apiThunk<any, { bookingId: number | string; cancelReasonId: number }>(
  "bookings/cancel",
  {
    server: "taxi",
    url: ({ bookingId }) => `supplier-bookings/${bookingId}/cancel`,
    method: "POST",
    body: ({ cancelReasonId }) => ({ cancel_reason_id: cancelReasonId }),
    successMsg: true,
    errorMsg: true,
  }
);

export const cancelScheduledRide = apiThunk<any, { scheduleId: number | string; reason?: string }>(
  "bookings/cancelScheduled",
  {
    server: "taxi",
    url: ({ scheduleId }) => `supplier-bookings/scheduled/${scheduleId}/cancel`,
    method: "POST",
    body: ({ reason }) => ({ reason: reason || null }),
    successMsg: true,
    errorMsg: true,
  }
);

/** Cancellation reasons for the ride's zone — the web's CancelBooking list. */
export const loadCancelReasons = apiThunk<any, { zoneId: number | string }>("bookings/cancelReasons", {
  server: "taxi",
  url: ({ zoneId }) => `cancelreason/list/${zoneId}`,
  method: "GET",
});

/** Online drivers near the pickup — decides which "now" cars can be booked (web CarsDisplay rule). */
export const loadNearbyDrivers = apiThunk<any, { latitude: number; longitude: number; radius?: number; current_zone?: number }>(
  "bookings/nearbyDrivers",
  {
    server: "login",
    url: "location/nearest-drivers",
    method: "POST",
    body: (arg) => ({ ...arg, eta: true }),
  }
);

interface BookingsState {
  list: any[];
  meta: any;
  scheduled: any[];
  statusCount: any;
  selected: any;
  /** Live driver position for the ride being watched, fed by the socket. */
  trackingPosition: any;
  cancelReasons: { id: number; title: string }[];
  errors: any;
  loading: boolean;
  saving: boolean;
}

const initialState: BookingsState = {
  list: [],
  meta: null,
  scheduled: [],
  statusCount: null,
  selected: null,
  trackingPosition: null,
  cancelReasons: [],
  errors: null,
  loading: false,
  saving: false,
};

const bookingsSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    clearSelectedBooking: (state) => {
      state.selected = null;
      state.trackingPosition = null;
    },
    /** Socket: driver position for the ride currently being watched. */
    updateTrackingPosition: (state, action: PayloadAction<any>) => {
      state.trackingPosition = action.payload;
    },
    /** Socket: a booking changed status — patch it in place without a refetch. */
    updateBookingStatus: (state, action: PayloadAction<any>) => {
      const incoming = action.payload;
      const id = incoming?.booking_id ?? incoming?.id;
      if (!id) return;
      const index = state.list.findIndex((b: any) => String(b.id) === String(id));
      if (index >= 0) state.list[index] = { ...state.list[index], ...incoming };
      if (state.selected && String(state.selected.id) === String(id)) {
        state.selected = { ...state.selected, ...incoming };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadBookings.pending, (state) => {
        state.errors = null;
        state.loading = true;
      })
      .addCase(loadBookings.fulfilled, (state, action) => {
        state.list = action.payload?.data ?? [];
        state.meta = action.payload?.meta ?? null;
        state.loading = false;
      })
      .addCase(loadBookings.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(loadScheduledRides.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadScheduledRides.fulfilled, (state, action) => {
        state.scheduled = action.payload?.data ?? [];
        state.meta = action.payload?.meta ?? null;
        state.loading = false;
      })
      .addCase(loadScheduledRides.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(loadStatusCount.fulfilled, (state, action) => {
        state.statusCount = action.payload?.data ?? null;
      })

      .addCase(loadBooking.fulfilled, (state, action) => {
        state.selected = action.payload?.data ?? null;
      })
      .addCase(loadScheduledRide.fulfilled, (state, action) => {
        state.selected = action.payload?.data ? { ...action.payload.data, is_schedule: true } : null;
      })

      .addCase(bookRide.pending, (state) => {
        state.errors = null;
        state.saving = true;
      })
      .addCase(bookRide.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(bookRide.rejected, (state, action) => {
        state.errors = action.payload;
        state.saving = false;
      })

      .addCase(cancelBooking.pending, (state) => {
        state.saving = true;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload?.data) state.selected = action.payload.data;
      })
      .addCase(cancelBooking.rejected, (state) => {
        state.saving = false;
      })
      .addCase(cancelScheduledRide.pending, (state) => {
        state.saving = true;
      })
      .addCase(cancelScheduledRide.fulfilled, (state) => {
        state.saving = false;
        if (state.selected) state.selected = { ...state.selected, status: "cancelled" };
      })
      .addCase(cancelScheduledRide.rejected, (state) => {
        state.saving = false;
      })

      .addCase(loadCancelReasons.fulfilled, (state, action) => {
        state.cancelReasons = action.payload?.data ?? [];
      });
  },
});

export const { clearSelectedBooking, updateTrackingPosition, updateBookingStatus } =
  bookingsSlice.actions;
export default bookingsSlice.reducer;
